import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Plus, Edit2, Trash2, Save, X } from 'lucide-react';

const AdminPricing = () => {
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [formData, setFormData] = useState({
    cloth_category: '',
    price: '',
    fixed_cost: '',
    ratio: ''
  });
  
  // États pour les prix des options
  const [optionsPricing, setOptionsPricing] = useState({ sanglon_price: 49.0 });
  const [editingSanglon, setEditingSanglon] = useState(false);
  const [sanglonPriceInput, setSanglonPriceInput] = useState('49.00');
  const [optionsError, setOptionsError] = useState('');
  const [optionsSuccess, setOptionsSuccess] = useState('');

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin/login');
      return;
    }
    fetchCategories();
    fetchOptionsPricing();
  }, [navigate]);

  const fetchCategories = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(`${BACKEND_URL}/api/admin/pricing/`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCategories(response.data);
      setLoading(false);
    } catch (err) {
      setError('Erreur lors du chargement des catégories');
      setLoading(false);
    }
  };

  const fetchOptionsPricing = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/options-pricing/`);
      setOptionsPricing(response.data);
      setSanglonPriceInput(response.data.sanglon_price.toFixed(2));
    } catch (err) {
      console.error('Erreur chargement prix options:', err);
    }
  };

  const handleUpdateSanglonPrice = async () => {
    try {
      setOptionsError('');
      setOptionsSuccess('');
      
      const newPrice = parseFloat(sanglonPriceInput);
      if (isNaN(newPrice) || newPrice < 0) {
        setOptionsError('Prix invalide');
        return;
      }

      await axios.put(`${BACKEND_URL}/api/options-pricing/`, {
        sanglon_price: newPrice
      });
      
      setOptionsPricing({ sanglon_price: newPrice });
      setEditingSanglon(false);
      setOptionsSuccess('Prix du sanglon mis à jour avec succès !');
      setTimeout(() => setOptionsSuccess(''), 3000);
    } catch (err) {
      setOptionsError('Erreur lors de la mise à jour du prix');
    }
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('admin_token');
      await axios.post(`${BACKEND_URL}/api/admin/pricing/`, {
        cloth_category: formData.cloth_category,
        price: parseFloat(formData.price),
        fixed_cost: parseFloat(formData.fixed_cost),
        ratio: parseFloat(formData.ratio)
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setShowAddForm(false);
      setFormData({ cloth_category: '', price: '', fixed_cost: '', ratio: '' });
      fetchCategories();
    } catch (err) {
      setError('Erreur lors de l\'ajout de la catégorie');
    }
  };

  const handleEdit = (category) => {
    setEditingId(category.id);
    setFormData({
      cloth_category: category.cloth_category,
      price: category.price.toString(),
      fixed_cost: category.fixed_cost.toString(),
      ratio: category.ratio.toString()
    });
  };

  const handleUpdate = async (id) => {
    try {
      const token = localStorage.getItem('admin_token');
      await axios.put(`${BACKEND_URL}/api/admin/pricing/${id}`, {
        cloth_category: formData.cloth_category,
        price: parseFloat(formData.price),
        fixed_cost: parseFloat(formData.fixed_cost),
        ratio: parseFloat(formData.ratio)
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEditingId(null);
      setFormData({ cloth_category: '', price: '', fixed_cost: '', ratio: '' });
      fetchCategories();
    } catch (err) {
      setError('Erreur lors de la mise à jour');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette catégorie ?')) return;
    
    try {
      const token = localStorage.getItem('admin_token');
      await axios.delete(`${BACKEND_URL}/api/admin/pricing/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchCategories();
    } catch (err) {
      setError('Erreur lors de la suppression');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    navigate('/admin/login');
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center">Chargement...</div>;
  }

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 py-4 flex justify-between items-center">
          <h1 className="text-2xl font-bold text-gray-800">Gestion des Prix</h1>
          <div className="flex gap-4">
            <button
              onClick={() => navigate('/admin/dashboard')}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
            >
              Dashboard
            </button>
            <button
              onClick={handleLogout}
              className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600"
            >
              Déconnexion
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 py-8">
        {error && (
          <div className="mb-4 p-4 bg-red-100 text-red-700 rounded">
            {error}
          </div>
        )}
        
        {/* Section Prix des Options */}
        <div className="mb-8 bg-white rounded-lg shadow p-6">
          <h2 className="text-xl font-semibold mb-4 text-gray-800">Prix des Options</h2>
          
          {optionsError && (
            <div className="mb-4 p-3 bg-red-100 text-red-700 rounded text-sm">
              {optionsError}
            </div>
          )}
          
          {optionsSuccess && (
            <div className="mb-4 p-3 bg-green-100 text-green-700 rounded text-sm">
              {optionsSuccess}
            </div>
          )}
          
          {/* Prix du Sanglon */}
          <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg">
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Prix unitaire d'un sanglon (€ TTC)
              </label>
              <p className="text-xs text-gray-500">
                Ce prix est appliqué à toutes les toiles de pergola avec l'option sanglons
              </p>
            </div>
            
            {editingSanglon ? (
              <>
                <input
                  type="number"
                  step="0.01"
                  value={sanglonPriceInput}
                  onChange={(e) => setSanglonPriceInput(e.target.value)}
                  className="w-32 px-3 py-2 border border-gray-300 rounded focus:outline-none focus:border-blue-500"
                />
                <button
                  onClick={handleUpdateSanglonPrice}
                  className="flex items-center gap-2 px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600"
                >
                  <Save size={16} />
                  Enregistrer
                </button>
                <button
                  onClick={() => {
                    setEditingSanglon(false);
                    setSanglonPriceInput(optionsPricing.sanglon_price.toFixed(2));
                    setOptionsError('');
                  }}
                  className="flex items-center gap-2 px-4 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400"
                >
                  <X size={16} />
                  Annuler
                </button>
              </>
            ) : (
              <>
                <div className="text-2xl font-bold text-gray-800">
                  {optionsPricing.sanglon_price.toFixed(2)} €
                </div>
                <button
                  onClick={() => setEditingSanglon(true)}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                >
                  <Edit2 size={16} />
                  Modifier
                </button>
              </>
            )}
          </div>
        </div>

        {/* Add Button */}
        <div className="mb-6 flex justify-between items-center">
          <h2 className="text-xl font-semibold">Nos collections</h2>
          <button
            onClick={() => setShowAddForm(!showAddForm)}
            className="flex items-center gap-2 px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600"
          >
            <Plus size={20} />
            Ajouter une catégorie
          </button>
        </div>

        {/* Add Form */}
        {showAddForm && (
          <div className="mb-6 p-6 bg-white rounded-lg shadow">
            <h3 className="text-lg font-semibold mb-4">Nouvelle Catégorie</h3>
            <form onSubmit={handleAdd} className="grid grid-cols-4 gap-4">
              <input
                type="text"
                placeholder="Catégorie de tissu"
                value={formData.cloth_category}
                onChange={(e) => setFormData({ ...formData, cloth_category: e.target.value })}
                className="px-4 py-2 border rounded"
                required
              />
              <input
                type="number"
                step="0.01"
                placeholder="Prix (€/m)"
                value={formData.price}
                onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                className="px-4 py-2 border rounded"
                required
              />
              <input
                type="number"
                step="0.01"
                placeholder="Coût fixe (€)"
                value={formData.fixed_cost}
                onChange={(e) => setFormData({ ...formData, fixed_cost: e.target.value })}
                className="px-4 py-2 border rounded"
                required
              />
              <input
                type="number"
                step="0.1"
                placeholder="Ratio"
                value={formData.ratio}
                onChange={(e) => setFormData({ ...formData, ratio: e.target.value })}
                className="px-4 py-2 border rounded"
                required
              />
              <div className="col-span-4 flex gap-2">
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                >
                  Ajouter
                </button>
                <button
                  type="button"
                  onClick={() => setShowAddForm(false)}
                  className="px-4 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400"
                >
                  Annuler
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Table */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Catégorie</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Prix (€/m)</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Coût fixe (€)</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Ratio</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase bg-green-50 text-green-700">Prix/m² client</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {categories.map((category) => (
                <tr key={category.id}>
                  {editingId === category.id ? (
                    <>
                      <td className="px-6 py-4">
                        <input
                          type="text"
                          value={formData.cloth_category}
                          onChange={(e) => setFormData({ ...formData, cloth_category: e.target.value })}
                          className="w-full px-2 py-1 border rounded"
                        />
                      </td>
                      <td className="px-6 py-4">
                        <input
                          type="number"
                          step="0.01"
                          value={formData.price}
                          onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                          className="w-full px-2 py-1 border rounded"
                        />
                      </td>
                      <td className="px-6 py-4">
                        <input
                          type="number"
                          step="0.01"
                          value={formData.fixed_cost}
                          onChange={(e) => setFormData({ ...formData, fixed_cost: e.target.value })}
                          className="w-full px-2 py-1 border rounded"
                        />
                      </td>
                      <td className="px-6 py-4">
                        <input
                          type="number"
                          step="0.1"
                          value={formData.ratio}
                          onChange={(e) => setFormData({ ...formData, ratio: e.target.value })}
                          className="w-full px-2 py-1 border rounded"
                        />
                      </td>
                      <td className="px-6 py-4 text-sm font-semibold text-green-700 bg-green-50">
                        {(parseFloat(formData.price || 0) * parseFloat(formData.ratio || 0)).toFixed(2)} €
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleUpdate(category.id)}
                            className="text-green-600 hover:text-green-800"
                          >
                            <Save size={18} />
                          </button>
                          <button
                            onClick={() => setEditingId(null)}
                            className="text-gray-600 hover:text-gray-800"
                          >
                            <X size={18} />
                          </button>
                        </div>
                      </td>
                    </>
                  ) : (
                    <>
                      <td className="px-6 py-4 text-sm text-gray-900">{category.cloth_category}</td>
                      <td className="px-6 py-4 text-sm text-gray-900">{category.price.toFixed(2)}</td>
                      <td className="px-6 py-4 text-sm text-gray-900">{category.fixed_cost.toFixed(2)}</td>
                      <td className="px-6 py-4 text-sm text-gray-900">{category.ratio.toFixed(1)}</td>
                      <td className="px-6 py-4 text-sm font-semibold text-green-700 bg-green-50">{(category.price * category.ratio).toFixed(2)} €</td>
                      <td className="px-6 py-4">
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleEdit(category)}
                            className="text-blue-600 hover:text-blue-800"
                          >
                            <Edit2 size={18} />
                          </button>
                          <button
                            onClick={() => handleDelete(category.id)}
                            className="text-red-600 hover:text-red-800"
                          >
                            <Trash2 size={18} />
                          </button>
                        </div>
                      </td>
                    </>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AdminPricing;
